import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

/**
 * Admin-facing modal: log a leave period on a team member's behalf.
 * Creates a pre-approved Leave row via POST /api/leaves/admin-log.
 *
 * Use case: recording sick days, unpaid absences, or any retrospective
 * entry payroll needs — without requiring the team member to submit a
 * request first.
 *
 * Props:
 *   open           boolean
 *   onClose        ()        — close the modal
 *   targetUser     { id, firstName, lastName } — who the leave is for
 *   onLogged       (leave)   — optional callback after successful log
 *                              so the parent can refresh balances/history
 */
const LogLeaveForUserModal = ({ open, onClose, targetUser, onLogged }) => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  const [leaveType, setLeaveType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [halfDay, setHalfDay] = useState(false);
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [overlapWarning, setOverlapWarning] = useState(null); // { overlapping: [...] }

  // Load the target user's effective leave types. Falls back to team
  // defaults if the user has no policies. This mirrors what the server
  // resolver will see when validating.
  useEffect(() => {
    if (!open || !targetUser?.id) return;
    setLoadingTypes(true);
    // /api/leaves/types returns the team's default leave types. If a user
    // is on a policy, their effective set can differ server-side, but we
    // show a superset here; the server rejects invalid picks with a clear
    // message.
    axios.get('/api/leaves/types')
      .then(r => setLeaveTypes(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLeaveTypes([]))
      .finally(() => setLoadingTypes(false));
  }, [open, targetUser?.id]);

  const reset = () => {
    setLeaveType('');
    setStartDate('');
    setEndDate('');
    setHalfDay(false);
    setNotes('');
    setOverlapWarning(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const submit = async (acknowledgeOverlap = false) => {
    if (!leaveType || !startDate || !endDate) {
      toast.error('Please fill in leave type and both dates');
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      toast.error('End date must be on or after start date');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        userId: targetUser.id,
        leaveType,
        startDate,
        endDate,
        halfDay,
        notes,
        acknowledgeOverlap
      };
      const resp = await axios.post('/api/leaves/admin-log', body);
      toast.success('Leave logged');
      if (onLogged) onLogged(resp.data.leave);
      reset();
      onClose();
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      if (status === 409 && data?.code === 'OVERLAP') {
        // Warn-and-allow: show the overlapping rows and let the admin
        // choose to continue anyway.
        setOverlapWarning({ overlapping: data.overlapping || [] });
      } else {
        toast.error(data?.message || 'Failed to log leave');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gray-900/60 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Log Leave for {targetUser?.firstName} {targetUser?.lastName}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Use this to record sick days, unpaid absences, or anything payroll
            needs. It will be saved as approved immediately and the user will
            be emailed a copy.
          </p>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Leave type</label>
            <select
              value={leaveType}
              onChange={e => setLeaveType(e.target.value)}
              disabled={submitting || loadingTypes}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              <option value="">{loadingTypes ? 'Loading…' : 'Select leave type…'}</option>
              {leaveTypes.map(lt => (
                <option key={lt.id} value={lt.id}>{lt.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Start</label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                disabled={submitting}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">End</label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                disabled={submitting}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm sm:text-sm"
              />
            </div>
          </div>

          <div className="flex items-center">
            <input
              id="adminlog-halfday"
              type="checkbox"
              checked={halfDay}
              onChange={e => setHalfDay(e.target.checked)}
              disabled={submitting}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="adminlog-halfday" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Half-day
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes (optional)</label>
            <textarea
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value.slice(0, 500))}
              disabled={submitting}
              placeholder="e.g. called in sick, unpaid leave for family emergency"
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm sm:text-sm"
            />
          </div>

          {overlapWarning && (
            <div className="p-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200 mb-2">
                This period overlaps {overlapWarning.overlapping.length} existing leave
                {overlapWarning.overlapping.length === 1 ? '' : 's'} for this user:
              </p>
              <ul className="text-xs text-amber-900 dark:text-amber-200 space-y-1 mb-2">
                {overlapWarning.overlapping.map(l => (
                  <li key={l.id}>
                    {new Date(l.startDate).toLocaleDateString()} – {new Date(l.endDate).toLocaleDateString()} ({l.status}{l.halfDay ? ', half-day' : ''})
                  </li>
                ))}
              </ul>
              <p className="text-xs text-amber-800 dark:text-amber-300">
                You can continue anyway if this is intentional (e.g. they
                called in sick during their holiday).
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="py-2 px-4 border border-gray-300 dark:border-gray-500 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          {overlapWarning ? (
            <button
              type="button"
              onClick={() => submit(true)}
              disabled={submitting}
              className="py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
            >
              {submitting ? 'Logging…' : 'Log anyway'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => submit(false)}
              disabled={submitting}
              className="py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? 'Logging…' : 'Log Leave'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogLeaveForUserModal;
